import React, { useState, useEffect } from 'react';
import DateInput from '../Common/DateInput';
import { flights, carriers, legs, routes } from '../../services/api';
import { formatDate } from '../../utils/dateFormat';

const flightTypes = { operational: 'Operational', marketing: 'Marketing' };

function LegRow({ leg, index, onEdit, onDelete, isReadOnly = false }) {
  return (
    <tr key={leg.leg_id || index} style={{ backgroundColor: '#f9f9f9', borderLeft: '4px solid #3498db' }}>
      <td colSpan="2" style={{ paddingLeft: '20px' }}>
        <strong>Leg {leg.leg_num}:</strong> {leg.dep_iata} → {leg.arr_iata}
      </td>
      <td>{leg.dep_iata} → {leg.arr_iata}</td>
      <td>
        <span className={`badge ${leg.leg_operational_leg_id ? 'badge-warning' : 'badge-success'}`}>
          {leg.leg_operational_leg_id ? 'Marketing' : 'Operational'}
        </span>
      </td>
      {!isReadOnly && (
        <td>
          <div className="actions">
            <button className="btn btn-sm btn-primary" onClick={() => onEdit(leg)}>Edit</button>
            <button className="btn btn-sm btn-danger" onClick={() => onDelete(leg.leg_id)}>Delete</button>
          </div>
        </td>
      )}
    </tr>
  );
}

function FlightForm({ formData, setFormData, flightEditingId, currentLegs, setCurrentLegs, onSave, onCancel }) {
  const [carriersList, setCarriersList] = useState([]);
  const [routesList, setRoutesList] = useState([]);
  const [operationalLegsList, setOperationalLegsList] = useState([]);
  const [showLegForm, setShowLegForm] = useState(false);
  const [editingLegId, setEditingLegId] = useState(null);
  const [legFormData, setLegFormData] = useState({
    leg_num: 1,
    leg_route_id: '',
    leg_operational_leg_id: null
  });
  const [legError, setLegError] = useState('');

  useEffect(() => {
    loadCarriers();
    loadRoutes();
    loadOperationalLegs();
    if (flightEditingId) {
      loadFlightLegs();
    }
  }, [flightEditingId]);

  const loadCarriers = async () => {
    try {
      const response = await carriers.getAll();
      setCarriersList(response.data);
    } catch (err) {
      console.error('Failed to load carriers', err);
    }
  };

  const loadRoutes = async () => {
    try {
      const response = await routes.getAll();
      setRoutesList(response.data);
    } catch (err) {
      console.error('Failed to load routes', err);
    }
  };

  const loadOperationalLegs = async () => {
    try {
      const response = await legs.getAll();
      // Filter to only show operational legs (those without leg_operational_leg_id)
      setOperationalLegsList(response.data.filter(l => !l.leg_operational_leg_id));
    } catch (err) {
      console.error('Failed to load operational legs', err);
    }
  };

  const loadFlightLegs = async () => {
    try {
      if (formData.flight_id) {
        const response = await legs.getAll();
        const flightLegs = response.data.filter(l => l.leg_flight_id === formData.flight_id);
        setCurrentLegs(flightLegs);
      }
    } catch (err) {
      console.error('Failed to load flight legs', err);
    }
  };

  const handleAddLeg = () => {
    setLegFormData({
      leg_num: (currentLegs.length > 0 ? Math.max(...currentLegs.map(l => l.leg_num)) : 0) + 1,
      leg_route_id: '',
      leg_operational_leg_id: null
    });
    setEditingLegId(null);
    setShowLegForm(true);
    setLegError('');
  };

  const handleEditLeg = (leg) => {
    setLegFormData({
      leg_num: leg.leg_num,
      leg_route_id: leg.leg_route_id,
      leg_operational_leg_id: leg.leg_operational_leg_id
    });
    setEditingLegId(leg.leg_id);
    setShowLegForm(true);
    setLegError('');
  };

  const handleSaveLeg = async () => {
    setLegError('');
    try {
      if (!legFormData.leg_route_id) {
        setLegError('Route is required');
        return;
      }

      // If marketing leg selected, ensure its route matches the operational leg route
      if (legFormData.leg_operational_leg_id) {
        const opLeg = operationalLegsList.find(l => l.leg_id === legFormData.leg_operational_leg_id);
        if (!opLeg) {
          setLegError('Selected operational leg not found');
          return;
        }
        if (opLeg.leg_operational_leg_id) {
          setLegError('Operational leg cannot itself be marketing');
          return;
        }
        if (opLeg.leg_route_id !== legFormData.leg_route_id) {
          setLegError('Route must match the operational leg route');
          return;
        }
      }

      const legData = {
        leg_flight_id: formData.flight_id || 0,
        leg_num: legFormData.leg_num,
        leg_route_id: legFormData.leg_route_id,
        leg_operational_leg_id: legFormData.leg_operational_leg_id || null
      };

      if (flightEditingId && editingLegId) {
        // Editing existing leg on existing flight
        await legs.update(editingLegId, legData);
        // Refresh legs after update
        const response = await legs.getAll();
        const flightLegs = response.data.filter(l => l.leg_flight_id === formData.flight_id);
        setCurrentLegs(flightLegs);
      } else if (flightEditingId && !editingLegId) {
        // Adding NEW leg to existing flight
        await legs.create(legData);
        // Refresh legs after create
        const response = await legs.getAll();
        const flightLegs = response.data.filter(l => l.leg_flight_id === formData.flight_id);
        setCurrentLegs(flightLegs);
      } else if (!flightEditingId) {
        // For new flight, store leg data temporarily (will be saved when flight is created)
        const newLeg = {
          leg_id: `temp_${Date.now()}`,
          ...legData
        };
        setCurrentLegs([...currentLegs, newLeg]);
      }

      setShowLegForm(false);
      setEditingLegId(null);
    } catch (err) {
      setLegError(err.response?.data?.error || 'Failed to save leg');
    }
  };

  const handleDeleteLeg = async (legId) => {
    if (!window.confirm('Are you sure you want to delete this leg?')) return;
    try {
      if (typeof legId === 'string' && legId.startsWith('temp_')) {
        // Remove temporary leg from new flight
        setCurrentLegs(currentLegs.filter(l => l.leg_id !== legId));
      } else {
        // Delete existing leg from database
        await legs.delete(legId);
        // Refresh legs after deletion
        const response = await legs.getAll();
        const flightLegs = response.data.filter(l => l.leg_flight_id === formData.flight_id);
        setCurrentLegs(flightLegs);
      }
    } catch (err) {
      setLegError(err.response?.data?.error || 'Failed to delete leg');
    }
  };

  const handleCancelLegForm = () => {
    setShowLegForm(false);
    setEditingLegId(null);
    setLegError('');
  };

  const getRouteName = (routeId) => {
    const route = routesList.find(r => r.route_id === parseInt(routeId));
    return route ? `${route.dep_iata} → ${route.arr_iata}` : '-';
  };

  const getOperationalFlightName = (legId) => {
    const leg = operationalLegsList.find(l => l.leg_id === legId);
    return leg ? `${leg.carrier_iata || leg.carrier_name} ${leg.flight_number}` : '-';
  };

  return (
    <div className="form-container">
      {/* Flight Details */}
      <div style={{ marginBottom: '30px', paddingBottom: '20px', borderBottom: '2px solid #e0e0e0' }}>
        <h3>Flight Details</h3>

        <div className="form-group">
          <label>Carrier *</label>
          <select
            value={formData.flight_carrier_id || ''}
            onChange={(e) => setFormData({ ...formData, flight_carrier_id: parseInt(e.target.value) })}
            required
            disabled={flightEditingId}
          >
            <option value="">Select carrier</option>
            {carriersList.map(carrier => (
              <option key={carrier.carrier_id} value={carrier.carrier_id}>
                {carrier.carrier_name}
              </option>
            ))}
          </select>
        </div>

        <div className="form-group">
          <label>Flight Number *</label>
          <input
            type="number"
            value={formData.flight_number || ''}
            onChange={(e) => setFormData({ ...formData, flight_number: parseInt(e.target.value) })}
            min={1}
            required
            disabled={flightEditingId}
          />
        </div>

        <div className="form-group">
          <label>Valid From</label>
          <DateInput
            value={formData.flight_valid_from || ''}
            onChange={(value) => setFormData({ ...formData, flight_valid_from: value })}
          />
        </div>

        <div className="form-group">
          <label>Valid To</label>
          <DateInput
            value={formData.flight_valid_to || ''}
            onChange={(value) => setFormData({ ...formData, flight_valid_to: value })}
          />
        </div>
      </div>

      {/* Legs Management */}
      {
        <div style={{ marginBottom: '30px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '15px' }}>
            <h3>Flight Legs</h3>
            <button className="btn btn-primary" onClick={handleAddLeg}>Add Leg</button>
          </div>

          {legError && <div className="error">{legError}</div>}

          {showLegForm && (
            <div style={{
              background: '#f8f9fa',
              padding: '20px',
              borderRadius: '8px',
              marginBottom: '20px',
              border: '1px solid #dee2e6'
            }}>
              <h4>{editingLegId ? 'Edit Leg' : 'Add New Leg'}</h4>

              <div className="form-group">
                <label>Leg Number *</label>
                <input
                  type="number"
                  value={legFormData.leg_num || ''}
                  onChange={(e) => setLegFormData({ ...legFormData, leg_num: parseInt(e.target.value) })}
                  min={1}
                  required
                />
              </div>

              <div className="form-group">
                <label>Route *</label>
                <select
                  value={legFormData.leg_route_id || ''}
                  onChange={(e) => setLegFormData({ ...legFormData, leg_route_id: parseInt(e.target.value) })}
                  required
                >
                  <option value="">Select route</option>
                  {[...routesList]
                    .sort((a, b) => `${a.dep_iata || ''}${a.arr_iata || ''}`.localeCompare(`${b.dep_iata || ''}${b.arr_iata || ''}`))
                    .map(route => (
                    <option key={route.route_id} value={route.route_id}>
                      {route.dep_iata} → {route.arr_iata}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Operational Leg (for code-sharing)</label>
                <select
                  value={legFormData.leg_operational_leg_id || ''}
                  onChange={(e) => setLegFormData({ ...legFormData, leg_operational_leg_id: e.target.value ? parseInt(e.target.value) : null })}
                >
                  <option value="">None (this is operational leg)</option>
                  {operationalLegsList
                    .filter(leg => leg.leg_id !== editingLegId)
                    .map(leg => (
                      <option key={leg.leg_id} value={leg.leg_id}>
                        {leg.carrier_iata || leg.carrier_name} {leg.flight_number} (Leg {leg.leg_num}): {leg.dep_iata} → {leg.arr_iata}
                      </option>
                    ))}
                </select>
              </div>

              <div className="form-actions">
                <button type="button" className="btn btn-success" onClick={handleSaveLeg}>Save Leg</button>
                <button type="button" className="btn btn-secondary" onClick={handleCancelLegForm}>Cancel</button>
              </div>
            </div>
          )}

          {currentLegs.length > 0 ? (
            <div className="table-container">
              <table>
                <thead>
                  <tr>
                    <th colSpan="2">Leg</th>
                    <th>Route</th>
                    <th>Type</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {currentLegs.map((leg, idx) => (
                    <LegRow
                      key={leg.leg_id}
                      leg={leg}
                      index={idx}
                      onEdit={handleEditLeg}
                      onDelete={handleDeleteLeg}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          ) : (
            <div style={{
              padding: '20px',
              background: '#f8f9fa',
              borderRadius: '4px',
              color: '#7f8c8d',
              textAlign: 'center'
            }}>
              No legs added yet. Click "Add Leg" to add flight legs.
            </div>
          )}
        </div>
      }
    </div>
  );
}

function FlightsList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    flight_carrier_id: '',
    flight_number: '',
    flight_valid_from: null,
    flight_valid_to: null
  });
  const [flightLegs, setFlightLegs] = useState({});
  const [currentLegs, setCurrentLegs] = useState([]);

  useEffect(() => {
    loadItems();
  }, []);

  const loadItems = async () => {
    try {
      setLoading(true);
      const [flightsRes, legsRes] = await Promise.all([
        flights.getAll(),
        legs.getAll()
      ]);
      setItems(flightsRes.data);
      
      // Group legs by flight_id and enrich with operational flight data
      const legsMap = {};
      legsRes.data.forEach(leg => {
        if (!legsMap[leg.leg_flight_id]) {
          legsMap[leg.leg_flight_id] = [];
        }
        
        // Find operational leg data if this is a marketing leg
        let enrichedLeg = { ...leg };
        if (leg.leg_operational_leg_id) {
          const operationalLeg = legsRes.data.find(l => l.leg_id === leg.leg_operational_leg_id);
          if (operationalLeg) {
            enrichedLeg.operational_carrier_iata = operationalLeg.carrier_iata;
            enrichedLeg.operational_carrier_name = operationalLeg.carrier_name;
            enrichedLeg.operational_flight_number = operationalLeg.flight_number;
          }
        }
        
        legsMap[leg.leg_flight_id].push(enrichedLeg);
      });
      setFlightLegs(legsMap);
      setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setFormData({
      flight_carrier_id: '',
      flight_number: '',
      flight_valid_from: null,
      flight_valid_to: null
    });
    setCurrentLegs([]);
    setEditingId(null);
    setShowForm(true);
  };

  const normalizeDateValue = (value) => {
    if (!value) return '';
    if (value instanceof Date) {
      const y = value.getUTCFullYear();
      const m = value.getUTCMonth() + 1;
      const d = value.getUTCDate();
      const pad = (n) => (n < 10 ? `0${n}` : `${n}`);
      return `${y}-${pad(m)}-${pad(d)}`;
    }
    const match = typeof value === 'string' ? value.match(/^(\d{4})-(\d{2})-(\d{2})/) : null;
    if (match) return `${match[1]}-${match[2]}-${match[3]}`;
    return '';
  };

  const handleEdit = (item) => {
    setFormData({
      flight_id: item.flight_id,
      flight_carrier_id: item.flight_carrier_id,
      flight_number: item.flight_number,
      flight_valid_from: normalizeDateValue(item.flight_valid_from),
      flight_valid_to: normalizeDateValue(item.flight_valid_to)
    });
    setCurrentLegs(flightLegs[item.flight_id] || []);
    setEditingId(item.flight_id);
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this flight and all its legs?')) return;
    try {
      // First, delete all legs associated with this flight
      const flightLegsToDelete = flightLegs[id] || [];
      for (const leg of flightLegsToDelete) {
        await legs.delete(leg.leg_id);
      }
      
      // Then delete the flight
      await flights.delete(id);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to delete item');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    try {
      if (editingId) {
        await flights.update(editingId, formData);
      } else {
        // Create new flight
        const createdFlight = await flights.create(formData);
        const flightId = createdFlight.data.id || createdFlight.data.flight_id;

        // Save any temporary legs with the new flight ID
        for (const leg of currentLegs) {
          if (typeof leg.leg_id === 'string' && leg.leg_id.startsWith('temp_')) {
            const legData = {
              leg_flight_id: flightId,
              leg_num: leg.leg_num,
              leg_route_id: leg.leg_route_id,
              leg_operational_leg_id: leg.leg_operational_leg_id || null
            };
            await legs.create(legData);
          }
        }
      }
      setShowForm(false);
      loadItems();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to save item');
    }
  };

  const handleCancel = () => {
    setShowForm(false);
    setFormData({
      flight_carrier_id: '',
      flight_number: '',
      flight_valid_from: null,
      flight_valid_to: null
    });
    setCurrentLegs([]);
    setEditingId(null);
    setError('');
  };

  if (loading) {
    return <div className="loading">Loading...</div>;
  }

  if (showForm) {
    return (
      <div>
        <div className="page-header">
          <h2>{editingId ? 'Edit' : 'Create'} Flight</h2>
        </div>
        {error && <div className="error">{error}</div>}
        <form onSubmit={handleSubmit} className="form-container">
          <FlightForm formData={formData} setFormData={setFormData} flightEditingId={editingId} currentLegs={currentLegs} setCurrentLegs={setCurrentLegs} onSave={handleSubmit} onCancel={handleCancel} />
          <div className="form-actions">
            <button type="submit" className="btn btn-success">Save Flight</button>
            <button type="button" className="btn btn-secondary" onClick={handleCancel}>Cancel</button>
          </div>
        </form>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h2>Flights</h2>
        <button className="btn btn-primary" onClick={handleCreate}>Add New Flight</button>
      </div>

      {error && <div className="error">{error}</div>}

      {items.length === 0 ? (
        <div className="empty-state">
          <h3>No items found</h3>
          <p>Click "Add New Flight" to create your first flight</p>
        </div>
      ) : (
        <div className="table-container">
          <table>
            <thead>
              <tr>
                <th>ID</th>
                <th>Flight</th>
                <th>Route</th>
                <th>Type</th>
                <th>Valid From</th>
                <th>Valid To</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const flightLegsData = flightLegs[item.flight_id] || [];
                const validFrom = item.flight_valid_from ? formatDate(item.flight_valid_from) : '-';
                const validTo = item.flight_valid_to ? formatDate(item.flight_valid_to) : '-';
                return flightLegsData.length > 0 ? (
                  flightLegsData.map((leg, idx) => (
                    <tr key={`${item.flight_id}_${leg.leg_id}`}>
                      {idx === 0 && (
                        <>
                          <td rowSpan={flightLegsData.length}>{item.flight_id}</td>
                          <td rowSpan={flightLegsData.length}>
                            <strong>{item.carrier_iata || item.carrier_name} {item.flight_number}</strong>
                          </td>
                        </>
                      )}
                      <td>
                        {leg.dep_iata} → {leg.arr_iata}
                        {leg.leg_operational_leg_id && (
                          <div style={{ fontSize: '0.85em', color: '#7f8c8d', marginTop: '4px' }}>
                            Operated as {leg.operational_carrier_iata || leg.operational_carrier_name} {leg.operational_flight_number}
                          </div>
                        )}
                      </td>
                      <td>
                        <span className={`badge ${leg.leg_operational_leg_id ? 'badge-warning' : 'badge-success'}`}>
                          {leg.leg_operational_leg_id ? 'Marketing' : 'Operational'}
                        </span>
                      </td>
                      {idx === 0 && (
                        <>
                          <td rowSpan={flightLegsData.length}>{validFrom}</td>
                          <td rowSpan={flightLegsData.length}>{validTo}</td>
                          <td rowSpan={flightLegsData.length}>
                            <div className="actions">
                              <button className="btn btn-primary" onClick={() => handleEdit(item)}>Edit</button>
                              <button className="btn btn-danger" onClick={() => handleDelete(item.flight_id)}>Delete</button>
                            </div>
                          </td>
                        </>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr key={item.flight_id}>
                    <td>{item.flight_id}</td>
                    <td><strong>{item.carrier_iata || item.carrier_name} {item.flight_number}</strong></td>
                    <td colSpan="3" style={{ color: '#999', fontStyle: 'italic' }}>No legs configured</td>
                    <td>{validFrom}</td>
                    <td>{validTo}</td>
                    <td>
                      <div className="actions">
                        <button className="btn btn-primary" onClick={() => handleEdit(item)}>Edit</button>
                        <button className="btn btn-danger" onClick={() => handleDelete(item.flight_id)}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

export default FlightsList;

